// ─────────────────────────────────────────────────────────────────────────────
// V2People — the People group in the v2 design: Staff, Attendance, Payroll.
// Endpoints are v1's:
//   /api/staff (GET, POST, PUT /:id, DELETE /:id)
//   /api/attendance/:staffId/:year/:month (GET), /api/attendance/:staffId (POST { date, status })
//   /api/payroll/:staffId (GET history, POST { month, year, workingDays, deductions }),
//   /api/payroll/:payrollId/mark-paid (PATCH)
// The payroll preview mirrors the SERVER formula (routes/payroll.js) so what you
// see is what gets saved — including its daily-wage behaviour (see note in UI).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Money, Ring, Segmented, Sheet, Skel, Switch, inr, useMedia } from "./primitives";
import { Field, PageHead, Stat } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ini = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const STATUS = {
  present: { label: "Present", short: "P", tone: "good" },
  absent: { label: "Absent", short: "A", tone: "bad" },
  "half-day": { label: "Half day", short: "½", tone: "warn" },
  leave: { label: "Leave", short: "L", tone: "info" },
};

function useStaff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await fetch(`${API_BASE}/api/staff`, { headers: auth() }); const d = await r.json(); setStaff(Array.isArray(d) ? d : []); }
    catch { setStaff([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { staff, loading, reload: load };
}

// Shared list for Attendance / Payroll
function PeopleList({ staff, loading, selId, onSelect, isNarrow, right }) {
  const [q, setQ] = useState("");
  const list = staff.filter((s) => !q.trim() || `${s.name} ${s.role} ${s.department}`.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => (a.status === b.status ? a.name.localeCompare(b.name) : a.status === "active" ? -1 : 1));
  return (
    <section className="v2-card v2c-listcard">
      <div className="v2-field--search v2c-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff" aria-label="Search staff" /></div>
      {loading ? <div className="v2-list" style={{ marginTop: 10 }}>{[0, 1, 2].map((i) => <Skel key={i} h={54} r={16} />)}</div>
        : list.length === 0 ? <div className="v2c-empty"><Duo name="people" size={44} /><span>{staff.length ? "No one matches." : "No staff yet."}</span></div>
        : <div className="v2c-list">{list.map((s) => (
          <button key={s._id} type="button" className={`v2l-row${s._id === selId && !isNarrow ? " is-sel" : ""}${s.status === "inactive" ? " is-dim" : ""}`} onClick={() => onSelect(s._id)}>
            <span className="v2-avatar v2-avatar--client" style={{ width: 38, height: 38, fontSize: 13.5 }}>{ini(s.name)}</span>
            <span className="v2-row-main"><span className="v2-row-title">{s.name}</span><span className="v2-row-sub">{[s.role, s.department].filter(Boolean).join(" · ")}</span></span>
            {right?.(s)}
          </button>))}</div>}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STAFF
// ══════════════════════════════════════════════════════════════════════════════
export function V2Staff({ showAlert }) {
  usePageTitle("Staff");
  const navigate = useNavigate();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("staff", "write");
  const { staff, loading, reload } = useStaff();
  const [filter, setFilter] = useState("active");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const newBtn = useRef(null);

  const active = staff.filter((s) => s.status === "active");
  const fixedCost = active.filter((s) => s.salaryType === "fixed").reduce((a, s) => a + (s.baseSalary || 0), 0);
  const daily = active.filter((s) => s.salaryType === "daily");
  const depts = new Set(active.map((s) => s.department).filter(Boolean));
  const shown = staff.filter((s) => (filter === "all" || s.status === filter) && (!q.trim() || `${s.name} ${s.role} ${s.department} ${s.phone}`.toLowerCase().includes(q.trim().toLowerCase())))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="v2p">
      <PageHead eyebrow="People" title="Staff" sub="Your team, their pay and how to reach them."
        actions={canWrite && <button ref={newBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setEditing("new")}><Glyph name="plus" size={17} stroke={2.2} /> Add staff</button>} />
      <div className="v2k-stats">
        <Stat label="Active staff" value={loading ? "—" : active.length} note={`${staff.length - active.length} inactive`} />
        <Stat label="Fixed salaries" value={loading ? "—" : inr(fixedCost)} note="Per month" />
        <Stat label="Daily-wage staff" value={loading ? "—" : daily.length} note={daily.length ? `${inr(daily.reduce((a, s) => a + (s.baseSalary || 0), 0))} per day combined` : "None"} />
        <Stat label="Departments" value={loading ? "—" : depts.size || "—"} note={[...depts].slice(0, 3).join(", ") || "Not set"} />
      </div>
      <div className="v2i-toolbar">
        <Segmented ariaLabel="Status" value={filter} onChange={setFilter} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "all", label: "All" }]} />
        <div className="v2-field--search v2i-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, role, department, phone" aria-label="Search staff" /></div>
      </div>
      {loading ? <div className="v2pp-grid">{[0, 1, 2].map((i) => <Skel key={i} h={190} r={26} />)}</div>
        : shown.length === 0 ? <section className="v2-card"><div className="v2c-empty"><Duo name="people" size={48} /><strong>{staff.length ? "No one here" : "No staff yet"}</strong><span>Add your team to track attendance and generate payslips.</span>{!staff.length && canWrite && <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => setEditing("new")}>Add staff</button>}</div></section>
        : <div className="v2pp-grid">{shown.map((s) => (
          <article key={s._id} className={`v2-card v2pp-card${s.status === "inactive" ? " is-dim" : ""}`}>
            <div className="v2r-top">
              <span className="v2-avatar v2-avatar--client" style={{ width: 48, height: 48, fontSize: 17 }}>{ini(s.name)}</span>
              <span className="v2-row-main"><span className="v2-row-title">{s.name}</span><span className="v2-row-sub">{[s.role, s.department].filter(Boolean).join(" · ")}</span></span>
              {canWrite && <MenuButton className="v2-icon-btn v2i-more" label={`Options for ${s.name}`} width={220} items={[
                { label: "Edit", glyph: "note", onSelect: () => setEditing(s) },
                "divider", { label: "Remove", glyph: "close", danger: true, onSelect: () => setRemoving(s) }]} />}
            </div>
            <div className="v2pp-pay"><Money value={s.baseSalary} /><span>{s.salaryType === "daily" ? "per day" : "per month"}</span></div>
            <div className="v2pp-meta">
              {s.status === "inactive" ? <span className="v2-status v2-status--neutral">Inactive</span> : <span className="v2-status v2-status--good"><Glyph name="check" size={12} stroke={2.4} /> Active</span>}
              <span>Joined {fmtDate(s.joiningDate)}</span>
              {s.phone && <a href={`tel:${s.phone}`}><Glyph name="phone" size={13} /> {s.phone}</a>}
            </div>
            <div className="v2pp-actions">
              <button type="button" className="v2-btn v2-btn--sm" onClick={() => navigate("/dashboard/staff/attendance", { state: { staffId: s._id } })}><Glyph name="calendar" size={15} /> Attendance</button>
              <button type="button" className="v2-btn v2-btn--sm" onClick={() => navigate("/dashboard/staff/payroll", { state: { staffId: s._id } })}><Glyph name="cash" size={15} /> Payroll</button>
            </div>
          </article>))}</div>}

      <StaffEditor value={editing} originRef={newBtn} onClose={() => setEditing(null)} onSaved={(m) => { setEditing(null); showAlert?.(m, "success"); reload(); }} />
      <Sheet open={!!removing} onClose={() => setRemoving(null)} width={440} title={`Remove ${removing?.name || ""}?`}
        footer={<><button type="button" className="v2-btn" onClick={() => setRemoving(null)}>Keep</button>
          <button type="button" className="v2-btn v2c-btn-danger" onClick={async () => {
            try { const r = await fetch(`${API_BASE}/api/staff/${removing._id}`, { method: "DELETE", headers: auth() }); const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || "Couldn't remove"); showAlert?.("Staff member removed", "success"); setRemoving(null); reload(); }
            catch (e) { setRemoving((x) => ({ ...x, error: e.message })); }
          }}>Remove</button></>}>
        <p className="v2c-remove-text">If they've just left, consider marking them <b>Inactive</b> instead — that keeps their attendance and payslips easy to find.</p>
        {removing?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {removing.error}</div>}
      </Sheet>
    </div>
  );
}

const EMPTY = () => ({ name: "", role: "", department: "", phone: "", joiningDate: new Date().toISOString().split("T")[0], salaryType: "fixed", baseSalary: "", status: "active" });
function StaffEditor({ value, originRef, onClose, onSaved }) {
  const edit = value && value !== "new" ? value : null;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!value) return; setErr("");
    setF(edit ? { name: edit.name, role: edit.role, department: edit.department || "", phone: edit.phone || "", joiningDate: edit.joiningDate?.split("T")[0] || "", salaryType: edit.salaryType, baseSalary: edit.baseSalary, status: edit.status } : EMPTY());
  }, [value]); // eslint-disable-line
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.name.trim() || !f.role.trim()) { setErr("Name and role are required."); return; }
    if (!(parseFloat(f.baseSalary) > 0)) { setErr("Enter a salary greater than 0."); return; }
    setBusy(true);
    try {
      const r = await fetch(edit ? `${API_BASE}/api/staff/${edit._id}` : `${API_BASE}/api/staff`, { method: edit ? "PUT" : "POST", headers: json(), body: JSON.stringify({ ...f, baseSalary: parseFloat(f.baseSalary) }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save");
      onSaved(edit ? "Staff member updated" : `${f.name} added`);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!value} onClose={onClose} originRef={edit ? undefined : originRef} width={560} title={edit ? "Edit staff member" : "Add staff member"}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button><button type="submit" form="v2pp-form" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Add staff"}</button></>}>
      <form id="v2pp-form" className="v2-form" onSubmit={save}>
        <Field label="Full name"><input className="v2-input" autoFocus value={f.name} onChange={set("name")} /></Field>
        <div className="v2-form-row">
          <Field label="Role"><input className="v2-input" value={f.role} onChange={set("role")} placeholder="e.g. Accountant" /></Field>
          <Field label="Department" optional><input className="v2-input" value={f.department} onChange={set("department")} placeholder="e.g. Accounts" /></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Phone" optional><input className="v2-input" type="tel" value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Joined on"><input className="v2-input" type="date" value={f.joiningDate} onChange={set("joiningDate")} /></Field>
        </div>
        <Field label="Paid"><Segmented ariaLabel="Salary type" value={f.salaryType} onChange={(v) => setF((p) => ({ ...p, salaryType: v }))} options={[{ value: "fixed", label: "Fixed monthly" }, { value: "daily", label: "Daily wage" }]} /></Field>
        <Field label={f.salaryType === "daily" ? "Rate per day" : "Salary per month"}>
          <span className="v2-input-wrap"><span className="v2-input-prefix" style={{ fontSize: 16 }}>₹</span><input className="v2-input" style={{ paddingLeft: 30 }} type="number" min="0" step="0.01" value={f.baseSalary} onChange={set("baseSalary")} /></span>
        </Field>
        <div className="v2-inset"><Switch id="v2pp-active" checked={f.status === "active"} onChange={(v) => setF((p) => ({ ...p, status: v ? "active" : "inactive" }))} label="Active" description="Inactive staff are hidden from attendance by default" /></div>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}

// Selected-person helper for Attendance / Payroll (honours state.staffId from Staff cards)
function useSelected(staff, isNarrow) {
  const location = useLocation();
  const [selId, setSelId] = useState(location.state?.staffId || null);
  useEffect(() => {
    if (!isNarrow && !selId && staff.length) { const first = staff.find((s) => s.status === "active") || staff[0]; setSelId(first._id); }
  }, [staff, isNarrow, selId]);
  return [selId, setSelId, staff.find((s) => s._id === selId) || null];
}

// ══════════════════════════════════════════════════════════════════════════════
// ATTENDANCE — paint a month
// ══════════════════════════════════════════════════════════════════════════════
export function V2Attendance() {
  usePageTitle("Attendance");
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("attendance", "write");
  const isNarrow = useMedia("(max-width: 900px)");
  const { staff, loading } = useStaff();
  const [selId, setSelId, sel] = useSelected(staff, isNarrow);
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [marks, setMarks] = useState({});
  const [busyDays, setBusyDays] = useState({});
  const [brush, setBrush] = useState("present");
  const [loadingM, setLoadingM] = useState(false);
  const [err, setErr] = useState("");
  const [filling, setFilling] = useState(null);

  const key = (d) => `${ym.y}-${String(ym.m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const load = useCallback(async () => {
    if (!selId) return;
    setLoadingM(true); setErr("");
    try {
      const r = await fetch(`${API_BASE}/api/attendance/${selId}/${ym.y}/${ym.m}`, { headers: auth() });
      const d = await r.json();
      const map = {}; (Array.isArray(d) ? d : []).forEach((e) => { map[new Date(e.date).toISOString().split("T")[0]] = e.status; });
      setMarks(map);
    } catch { setMarks({}); setErr("Couldn't load attendance."); } finally { setLoadingM(false); }
  }, [selId, ym]);
  useEffect(() => { load(); }, [load]);

  const mark = async (d, status) => {
    const k = key(d);
    const prev = marks[k];
    if (prev === status) return;
    setMarks((m) => ({ ...m, [k]: status })); setBusyDays((b) => ({ ...b, [k]: true }));
    try {
      const r = await fetch(`${API_BASE}/api/attendance/${selId}`, { method: "POST", headers: json(), body: JSON.stringify({ date: k, status }) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || "Couldn't save");
    } catch (x) { setMarks((m) => { const n = { ...m }; if (prev) n[k] = prev; else delete n[k]; return n; }); setErr(x.message); }
    finally { setBusyDays((b) => { const n = { ...b }; delete n[k]; return n; }); }
  };

  const days = new Date(ym.y, ym.m, 0).getDate();
  const lead = (new Date(ym.y, ym.m - 1, 1).getDay() + 6) % 7; // Monday-first
  const isFuture = (d) => new Date(ym.y, ym.m - 1, d) > now;
  const isSunday = (d) => new Date(ym.y, ym.m - 1, d).getDay() === 0;
  const atLatest = ym.y > now.getFullYear() || (ym.y === now.getFullYear() && ym.m >= now.getMonth() + 1);
  const step = (dir) => setYm(({ y, m }) => { const dt = new Date(y, m - 1 + dir, 1); return { y: dt.getFullYear(), m: dt.getMonth() + 1 }; });
  const vals = Object.values(marks);
  const count = (s) => vals.filter((v) => v === s).length;
  const effective = count("present") + count("half-day") * 0.5;
  const pastDays = Array.from({ length: days }, (_, i) => i + 1).filter((d) => !isFuture(d));
  const unmarkedWeekdays = pastDays.filter((d) => !isSunday(d) && !marks[key(d)]);

  const fill = async () => {
    setFilling({ done: 0, total: unmarkedWeekdays.length });
    for (const d of unmarkedWeekdays) { await mark(d, "present"); setFilling((f) => ({ ...f, done: f.done + 1 })); }
    setFilling(null);
  };

  const detail = sel && (
    <div className="v2l-detail" key={sel._id}>
      <div className="v2l-hero">
        <span className="v2-avatar v2-avatar--client" style={{ width: 52, height: 52, fontSize: 18 }}>{ini(sel.name)}</span>
        <div className="v2l-hero-text"><h2>{sel.name}</h2><span className="v2-row-sub">{[sel.role, sel.department].filter(Boolean).join(" · ")}</span></div>
        <div className="v2-stepper v2-glass" role="group" aria-label="Month">
          <button type="button" aria-label="Previous month" onClick={() => step(-1)}><Glyph name="chevronLeft" size={16} stroke={2} /></button>
          <span className="v2-stepper-label">{MONTHS[ym.m - 1].slice(0, 3)} {ym.y}</span>
          <button type="button" aria-label="Next month" disabled={atLatest} onClick={() => step(1)}><Glyph name="chevronRight" size={16} stroke={2} /></button>
        </div>
      </div>
      <div className="v2at-summary">
        <Ring value={pastDays.length ? (effective / pastDays.filter((d) => !isSunday(d)).length) * 100 : 0} size={84} stroke={9} color="var(--v2-good)" label={`${effective} effective days`}>
          <strong>{effective}</strong><span>days</span>
        </Ring>
        <div className="v2at-counts">{Object.entries(STATUS).map(([k, s]) => (
          <span key={k} className={`v2at-count v2at-count--${s.tone}`}><b>{count(k)}</b>{s.label}</span>))}</div>
      </div>
      {canWrite && (
        <div className="v2at-tools">
          <span className="v2-label">Tap days to mark</span>
          <div className="v2at-brush" role="radiogroup" aria-label="Status to paint">
            {Object.entries(STATUS).map(([k, s]) => (
              <button key={k} type="button" role="radio" aria-checked={brush === k} className={`v2at-brush-btn v2at-brush-btn--${s.tone}${brush === k ? " is-on" : ""}`} onClick={() => setBrush(k)}>
                <span>{s.short}</span>{s.label}
              </button>))}
          </div>
          {unmarkedWeekdays.length > 0 && !filling && <button type="button" className="v2k-link" onClick={fill}>Mark {unmarkedWeekdays.length} unmarked weekday{unmarkedWeekdays.length === 1 ? "" : "s"} present</button>}
          {filling && <span className="v2-row-sub"><span className="v2-spin" /> Marking {filling.done}/{filling.total}…</span>}
        </div>
      )}
      {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      <div className={`v2at-cal${loadingM ? " is-refreshing" : ""}`}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d} className="v2at-dow">{d}</span>)}
        {Array.from({ length: lead }).map((_, i) => <span key={`b${i}`} />)}
        {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
          const s = marks[key(d)]; const fut = isFuture(d); const today = ym.y === now.getFullYear() && ym.m === now.getMonth() + 1 && d === now.getDate();
          return (
            <button key={d} type="button" disabled={fut || !canWrite} aria-label={`${d} ${MONTHS[ym.m - 1]}: ${s ? STATUS[s].label : "not marked"}`}
              className={`v2at-day${s ? ` v2at-day--${STATUS[s].tone}` : ""}${fut ? " is-future" : ""}${isSunday(d) ? " is-sun" : ""}${today ? " is-today" : ""}${busyDays[key(d)] ? " is-busy" : ""}`}
              onClick={() => mark(d, brush)}>
              <span className="v2at-num">{d}</span>{s && <span className="v2at-tag">{STATUS[s].short}</span>}
            </button>);
        })}
      </div>
      <p className="v2-row-sub">Sundays are shaded. Each tap saves immediately; tap again with another status to change it.</p>
    </div>
  );
  const detailOnly = isNarrow && sel;
  return (
    <div className="v2p v2l">
      {!detailOnly && <PageHead eyebrow="People" title="Attendance" sub="Mark presence day by day — it feeds straight into payroll." />}
      {detailOnly && <button type="button" className="v2l-back" onClick={() => setSelId(null)}><Glyph name="chevronLeft" size={18} stroke={2} /> All staff</button>}
      <div className={`v2c-split${detailOnly ? " is-detail" : ""}`}>
        {!detailOnly && <PeopleList staff={staff} loading={loading} selId={selId} onSelect={setSelId} isNarrow={isNarrow} />}
        {(!isNarrow || sel) && <section className={isNarrow ? "" : "v2-card v2c-detailcard"}>{detail || <div className="v2c-empty"><Duo name="calendar" size={44} /><span>{staff.length ? "Choose someone." : "Add staff first."}</span></div>}</section>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAYROLL
// ══════════════════════════════════════════════════════════════════════════════
export function V2Payroll({ showAlert }) {
  usePageTitle("Payroll");
  const { user, hasPermission } = useContext(userContext);
  const canWrite = hasPermission("payroll", "write");
  const isNarrow = useMedia("(max-width: 900px)");
  const { staff, loading } = useStaff();
  const [selId, setSelId, sel] = useSelected(staff, isNarrow);
  const [history, setHistory] = useState(null);
  const [gen, setGen] = useState(false);
  const [payslip, setPayslip] = useState(null);
  const [paying, setPaying] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!selId) return;
    setHistory(null);
    try { const r = await fetch(`${API_BASE}/api/payroll/${selId}`, { headers: auth() }); const d = await r.json(); setHistory(Array.isArray(d) ? d.sort((a, b) => b.year - a.year || b.month - a.month) : []); }
    catch { setHistory([]); }
  }, [selId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const unpaid = (history || []).filter((p) => !p.paid);
  const detail = sel && (
    <div className="v2l-detail" key={sel._id}>
      <div className="v2l-hero">
        <span className="v2-avatar v2-avatar--client" style={{ width: 52, height: 52, fontSize: 18 }}>{ini(sel.name)}</span>
        <div className="v2l-hero-text"><h2>{sel.name}</h2><span className="v2-row-sub">{inr(sel.baseSalary, { decimals: 2 })} {sel.salaryType === "daily" ? "per day" : "per month"} · {sel.role}</span></div>
        {canWrite && <div className="v2l-hero-actions"><button type="button" className="v2-btn v2-btn--primary" onClick={() => setGen(true)}><Glyph name="plus" size={16} stroke={2.2} /> Generate payslip</button></div>}
      </div>
      <div className="v2l-stats">
        <div className="v2l-stat"><span>Payslips</span><strong>{history ? history.length : "—"}</strong></div>
        <div className="v2l-stat"><span>Paid this year</span><strong className="is-good">{inr((history || []).filter((p) => p.paid && p.year === new Date().getFullYear()).reduce((a, p) => a + p.netSalary, 0))}</strong></div>
        <div className={`v2l-stat v2l-stat--hero${unpaid.length ? "" : " is-clear"}`}><span>{unpaid.length ? `${unpaid.length} unpaid` : "All paid"}</span><strong><Money value={unpaid.reduce((a, p) => a + p.netSalary, 0)} /></strong></div>
      </div>
      <div className="v2c-section-title">Payslips</div>
      <div className="v2-card v2l-statement">
        {history === null ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2].map((i) => <Skel key={i} h={52} r={14} />)}</div>
          : history.length === 0 ? <div className="v2c-empty"><Duo name="cash" size={44} /><span>No payslips yet. Generate one after marking the month's attendance.</span></div>
          : history.map((p) => (
            <div key={p._id} className="v2l-entry v2py-row">
              <span className="v2pub-day"><strong>{MONTHS[p.month - 1].slice(0, 3)}</strong><span>{p.year}</span></span>
              <button type="button" className="v2l-entry-main v2py-open" onClick={() => setPayslip(p)}>
                <span className="v2l-entry-desc">{MONTHS[p.month - 1]} {p.year}</span>
                <span className="v2l-entry-meta">{p.presentDays} of {p.workingDays} days{p.deductions ? ` · −${inr(p.deductions)} deductions` : ""}</span>
              </button>
              <span className="v2l-entry-amts">
                <span className="v2l-amt">{inr(p.netSalary, { decimals: 2 })}</span>
                {p.paid ? <span className="v2-status v2-status--good"><Glyph name="check" size={12} stroke={2.4} /> Paid {p.paidOn ? fmtDate(p.paidOn) : ""}</span>
                  : canWrite ? <button type="button" className="v2-btn v2-btn--sm" onClick={() => setPaying(p)}>Mark paid</button>
                  : <span className="v2-status v2-status--warn">Unpaid</span>}
              </span>
            </div>))}
      </div>
    </div>
  );
  const detailOnly = isNarrow && sel;
  return (
    <div className="v2p v2l">
      {!detailOnly && <PageHead eyebrow="People" title="Payroll" sub="Monthly payslips from attendance — generate, pay, print." />}
      {detailOnly && <button type="button" className="v2l-back" onClick={() => setSelId(null)}><Glyph name="chevronLeft" size={18} stroke={2} /> All staff</button>}
      <div className={`v2c-split${detailOnly ? " is-detail" : ""}`}>
        {!detailOnly && <PeopleList staff={staff} loading={loading} selId={selId} onSelect={setSelId} isNarrow={isNarrow}
          right={(s) => <span className="v2l-row-bal">{s.salaryType === "daily" ? "daily" : "monthly"}</span>} />}
        {(!isNarrow || sel) && <section className={isNarrow ? "" : "v2-card v2c-detailcard"}>{detail || <div className="v2c-empty"><Duo name="cash" size={44} /><span>{staff.length ? "Choose someone." : "Add staff first."}</span></div>}</section>}
      </div>
      {sel && <GenerateSheet open={gen} staff={sel} existing={history || []} onClose={() => setGen(false)}
        onDone={(p) => { setGen(false); showAlert?.(`Payslip for ${MONTHS[p.month - 1]} ${p.year} generated`, "success"); loadHistory(); setPayslip(p); }} />}
      <PayslipSheet p={payslip} staff={sel} company={user} onClose={() => setPayslip(null)} />
      <Sheet open={!!paying} onClose={() => setPaying(null)} width={420} title="Mark as paid?" subtitle={paying ? `${sel?.name} · ${MONTHS[paying.month - 1]} ${paying.year}` : ""}
        footer={<><button type="button" className="v2-btn" onClick={() => setPaying(null)}>Not yet</button>
          <button type="button" className="v2-btn v2-btn--primary" onClick={async () => {
            try { const r = await fetch(`${API_BASE}/api/payroll/${paying._id}/mark-paid`, { method: "PATCH", headers: auth() }); const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || "Couldn't update"); showAlert?.("Marked paid", "success"); setPaying(null); loadHistory(); }
            catch (e) { setPaying((x) => ({ ...x, error: e.message })); }
          }}>Mark {paying ? inr(paying.netSalary, { decimals: 2 }) : ""} paid</button></>}>
        <p className="v2c-remove-text">Records today as the payment date. It counts toward payroll paid on the dashboard.</p>
        {paying?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {paying.error}</div>}
      </Sheet>
    </div>
  );
}

function GenerateSheet({ open, staff, existing, onClose, onDone }) {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [workingDays, setWorkingDays] = useState(26);
  const [deductions, setDeductions] = useState("");
  const [present, setPresent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setYm({ y: now.getFullYear(), m: now.getMonth() + 1 }); setWorkingDays(26); setDeductions(""); setErr(""); } }, [open]); // eslint-disable-line
  useEffect(() => {
    if (!open) return;
    setPresent(null);
    fetch(`${API_BASE}/api/attendance/${staff._id}/${ym.y}/${ym.m}`, { headers: auth() }).then((r) => r.json())
      .then((d) => setPresent((Array.isArray(d) ? d : []).reduce((s, a) => s + (a.status === "present" ? 1 : a.status === "half-day" ? 0.5 : 0), 0)))
      .catch(() => setPresent(0));
  }, [open, ym, staff._id]);
  const wd = parseInt(workingDays) || 0, ded = parseFloat(deductions) || 0;
  // ↓ identical to routes/payroll.js
  const gross = staff.salaryType === "fixed" ? staff.baseSalary : (staff.baseSalary || 0) * (present || 0);
  const net = gross - ded;
  const dup = existing.some((p) => p.month === ym.m && p.year === ym.y);
  const step = (dir) => setYm(({ y, m }) => { const dt = new Date(y, m - 1 + dir, 1); return { y: dt.getFullYear(), m: dt.getMonth() + 1 }; });
  const go = async () => {
    setErr("");
    if (!(wd > 0)) { setErr("Working days must be at least 1."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/payroll/${staff._id}`, { method: "POST", headers: json(), body: JSON.stringify({ month: ym.m, year: ym.y, workingDays: wd, deductions: ded }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't generate");
      onDone(d);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} width={520} title="Generate payslip" subtitle={staff.name}
      footer={<><span className="v2cn-foot-total">Net <b>{inr(net, { decimals: 2 })}</b></span><button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="v2-btn v2-btn--primary" disabled={busy || dup || present === null} onClick={go}>{busy ? "Generating…" : "Generate"}</button></>}>
      <div className="v2-form">
        <div className="v2py-month">
          <div className="v2-stepper v2-glass" role="group" aria-label="Month">
            <button type="button" aria-label="Previous month" onClick={() => step(-1)}><Glyph name="chevronLeft" size={16} stroke={2} /></button>
            <span className="v2-stepper-label">{MONTHS[ym.m - 1]} {ym.y}</span>
            <button type="button" aria-label="Next month" onClick={() => step(1)}><Glyph name="chevronRight" size={16} stroke={2} /></button>
          </div>
          {dup && <span className="v2-status v2-status--warn">Already generated</span>}
        </div>
        <div className="v2-form-row">
          <Field label="Working days" hint="Days the business was open this month"><input className="v2-input" type="number" min="1" max="31" value={workingDays} onChange={(e) => setWorkingDays(e.target.value)} /></Field>
          <Field label="Deductions" optional hint="Advances, PF, fines…"><span className="v2-input-wrap"><span className="v2-input-prefix" style={{ fontSize: 16 }}>₹</span><input className="v2-input" style={{ paddingLeft: 30 }} type="number" min="0" step="0.01" value={deductions} onChange={(e) => setDeductions(e.target.value)} /></span></Field>
        </div>
        <div className="v2-inset v2cn-totals">
          <div className="v2-inset-row v2-inset-row--static"><span>Days present (from attendance)</span><b>{present === null ? "…" : present}</b></div>
          <div className="v2-inset-row v2-inset-row--static"><span>Gross</span><b>{inr(gross, { decimals: 2 })}</b></div>
          {ded > 0 && <div className="v2-inset-row v2-inset-row--static"><span>Deductions</span><b>−{inr(ded, { decimals: 2 })}</b></div>}
          <div className="v2-inset-row v2-inset-row--static v2pub-grand"><span>Net pay</span><b>{inr(net, { decimals: 2 })}</b></div>
        </div>
        {staff.salaryType === "fixed"
          ? <p className="v2-row-sub">Fixed salary is paid in full regardless of attendance (current server rule).</p>
          : <p className="v2-row-sub">Daily-wage pay = rate per day × days present ({inr(staff.baseSalary)} × {present ?? "—"}).</p>}
        {present === 0 && <div className="v2-banner v2-banner--warn"><Glyph name="calendar" size={16} /> No attendance marked for {MONTHS[ym.m - 1]} yet.</div>}
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </div>
    </Sheet>
  );
}

function PayslipSheet({ p: live, staff, company, onClose }) {
  const keep = useRef(live); if (live) keep.current = live;
  const p = keep.current;
  if (!p) return null;
  const print = () => {
    document.documentElement.setAttribute("data-v2-print", "payslip");
    const done = () => { document.documentElement.removeAttribute("data-v2-print"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    window.print();
  };
  return (
    <Sheet open={!!live} onClose={onClose} width={560} title="Payslip" subtitle={`${staff?.name || ""} · ${MONTHS[p.month - 1]} ${p.year}`}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Close</button><button type="button" className="v2-btn v2-btn--primary" onClick={print}><Glyph name="invoice" size={15} /> Print</button></>}>
      <div id="v2-payslip" className="v2py-slip">
        <div className="v2py-slip-head">
          <div><strong>{company?.companyName || "Company"}</strong><span>{company?.companyAddress}</span></div>
          <div className="v2py-slip-title"><span>Payslip</span><strong>{MONTHS[p.month - 1]} {p.year}</strong></div>
        </div>
        <dl className="v2pub-meta">
          <div><dt>Employee</dt><dd>{staff?.name}</dd></div>
          <div><dt>Role</dt><dd>{staff?.role || "—"}</dd></div>
          <div><dt>Days present</dt><dd>{p.presentDays} / {p.workingDays}</dd></div>
          <div><dt>Status</dt><dd>{p.paid ? `Paid ${fmtDate(p.paidOn)}` : "Unpaid"}</dd></div>
        </dl>
        <div className="v2-inset v2cn-totals">
          <div className="v2-inset-row v2-inset-row--static"><span>Gross salary</span><b>{inr(p.grossSalary, { decimals: 2 })}</b></div>
          <div className="v2-inset-row v2-inset-row--static"><span>Deductions</span><b>−{inr(p.deductions, { decimals: 2 })}</b></div>
          <div className="v2-inset-row v2-inset-row--static v2pub-grand"><span>Net pay</span><b>{inr(p.netSalary, { decimals: 2 })}</b></div>
        </div>
        <p className="v2-row-sub">Computer-generated payslip · {staff?.salaryType === "daily" ? `Daily rate ${inr(staff?.baseSalary, { decimals: 2 })}` : `Monthly salary ${inr(staff?.baseSalary, { decimals: 2 })}`}</p>
      </div>
    </Sheet>
  );
}
